import {
  IonBackButton,
  IonButton,
  IonButtons,
  IonCard,
  IonCardContent,
  IonContent,
  IonHeader,
  IonIcon,
  IonPage,
  IonText,
  IonTitle,
  IonToolbar,
} from '@ionic/react';
import {
  barChartOutline,
  closeOutline,
  playCircleOutline,
} from 'ionicons/icons';
import './MachineItem.css';
import { firestore, auth } from '../../../firebase';
import { useEffect, useState } from 'react';
import {
  doc,
  getDoc,
} from 'firebase/firestore';
import { useRouteMatch } from 'react-router';
import { Exercise, Machines } from '../../../datamodels';
import MachineExerciseChart from '../../statistics/machine_exercise_chart/MachineExerciseChart';
import { machineSessionRepository } from '../../../repositories/LocalRepositories';
import { CONSENT_EVENT, ConsentChoice, readConsent } from '../../../components/consent/ConsentBanner';
import {
  getMachineImageUrl,
  MACHINE_FALLBACK_IMAGE,
} from '../../../utils/MachineImageUtils';

interface RouteParams {
  id: string;
}
const MachineItem: React.FC = () => {
  const { id } = useRouteMatch<RouteParams>().params;
  const [machine, setMachine] = useState<Machines | null>(null);
  const [showChart, setShowChart] = useState<Record<number, boolean>>({});
  const [chartData, setChartData] = useState<Record<number, any[]>>({});
  const [brokenImage, setBrokenImage] = useState(false);
  const [videoExercise, setVideoExercise] = useState<Exercise | null>(null);
  const [allowYouTube, setAllowYouTube] = useState(false);
  const [externalMediaAllowed, setExternalMediaAllowed] = useState(() => readConsent()?.externalMedia === true);

  useEffect(() => {
    const updateConsent = (event: Event) => setExternalMediaAllowed(Boolean((event as CustomEvent<ConsentChoice>).detail?.externalMedia));
    window.addEventListener(CONSENT_EVENT, updateConsent);
    return () => window.removeEventListener(CONSENT_EVENT, updateConsent);
  }, []);

  useEffect(() => {
    getDoc(doc(firestore, 'machines', id)).then(found => {
      if (found.exists())
        setMachine({ id: found.id, ...found.data() } as Machines);
    });
  }, [id]);

  const fetchChartData = async (exerciseName: string, index: number) => {
    const userId = auth.currentUser?.uid;
    if (!userId || !machine) return;
    const sessions = await machineSessionRepository.list(userId);
    const setsByDate: any[] = [];
    sessions.forEach(data => {
      if (
        data.machine_ref.id === machine.id &&
        data.exercise_name === exerciseName
      ) {
        const date = data.date_used.toDate().toISOString().split('T')[0];
        data.sets.forEach((set: any) =>
          setsByDate.push({ date, weight: set.weight, reps: set.reps })
        );
      }
    });
    setsByDate.sort((a, b) => a.date.localeCompare(b.date));
    setChartData(current => ({ ...current, [index]: setsByDate }));
  };

  const handleToggleChart = async (index: number, exerciseName: string) => {
    if (!showChart[index] && !chartData[index])
      await fetchChartData(exerciseName, index);
    setShowChart(current => ({ ...current, [index]: !current[index] }));
  };

  const imageUrl = getMachineImageUrl(machine);
  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            <IonBackButton defaultHref="/my/machines" text="" />
          </IonButtons>
          <IonTitle>{machine?.title || 'Machine'}</IonTitle>
        </IonToolbar>
      </IonHeader>

      <IonContent fullscreen className="machine-detail-content">
        <section className="machine-detail-hero">
          <div className="machine-detail-hero__image">
            {imageUrl && !brokenImage ? (
              <img
                src={imageUrl}
                alt={machine?.title || 'Machine'}
                onError={() => setBrokenImage(true)}
              />
            ) : (
              <img src={MACHINE_FALLBACK_IMAGE} alt="" className="is-fallback" />
            )}
          </div>
          <div>
            <span>Exercise machine</span>
            <h1>{machine?.title || 'Loading machine…'}</h1>
            <p>{machine?.exercises?.length || 0} exercise options</p>
          </div>
        </section>

        <div className="machine-detail-heading">
          <span>Choose an exercise</span>
          <h2>Technique &amp; progress</h2>
        </div>

        <div className="machine-exercise-list">
          {(machine?.exercises || []).map((exercise, index) => {
            const hasVideo = Boolean(
              exercise.video?.videoId || exercise.video?.sourceUrl
            );
            const isVideoOpen = videoExercise === exercise;
            const videoId = exercise.video?.videoId;
            return (
              <IonCard
                className="machine-exercise-card"
                key={exercise.id || `${exercise.name}-${index}`}
              >
                <IonCardContent>
                  <div className="machine-exercise-card__heading">
                    <div>
                      <div className="machine-exercise-card__muscles">
                        {(exercise.muscles || []).map(muscle => (
                          <span key={muscle}>{muscle}</span>
                        ))}
                      </div>
                      <h3>{exercise.name}</h3>
                    </div>
                  </div>

                  <div className="machine-exercise-card__actions">
                    <IonButton
                      fill={hasVideo ? 'solid' : 'outline'}
                      disabled={!hasVideo}
                      onClick={() => {
                        setVideoExercise(isVideoOpen ? null : exercise);
                        setAllowYouTube(false);
                      }}
                    >
                      <IonIcon icon={playCircleOutline} slot="start" />
                      {isVideoOpen
                        ? 'Close video'
                        : hasVideo
                        ? 'Watch execution'
                        : 'Video coming soon'}
                    </IonButton>
                    <IonButton
                      fill="outline"
                      onClick={() => handleToggleChart(index, exercise.name)}
                    >
                      <IonIcon icon={barChartOutline} slot="start" />
                      {showChart[index] ? 'Hide progress' : 'View progress'}
                    </IonButton>
                  </div>

                  {isVideoOpen && (
                    <div className="exercise-video-panel">
                      <div className="exercise-video-panel__heading">
                        <div>
                          <span>Technique guide</span>
                          <h4>{exercise.name}</h4>
                        </div>
                        <IonButton
                          fill="clear"
                          onClick={() => {
                            setVideoExercise(null);
                            setAllowYouTube(false);
                          }}
                          aria-label="Close video"
                        >
                          <IonIcon icon={closeOutline} slot="icon-only" />
                        </IonButton>
                      </div>

                      {!externalMediaAllowed && !allowYouTube ? (
                        <div className="exercise-video-consent">
                          <IonIcon icon={playCircleOutline} />
                          <h4>Ready to watch?</h4>
                          <p>
                            The video loads only when you choose to play it.
                            YouTube may then share data with Google.
                          </p>
                          {videoId ? (
                            <IonButton
                              expand="block"
                              onClick={() => setAllowYouTube(true)}
                            >
                              Play here
                            </IonButton>
                          ) : (
                            <IonButton
                              expand="block"
                              href={exercise.video?.sourceUrl}
                              target="_blank"
                              rel="noreferrer"
                            >
                              Open video
                            </IonButton>
                          )}
                        </div>
                      ) : videoId ? (
                        <div className="exercise-video-frame">
                          <iframe
                            src={`https://www.youtube-nocookie.com/embed/${encodeURIComponent(
                              videoId
                            )}?playsinline=1&rel=0`}
                            title={`${exercise.name} execution video`}
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                            allowFullScreen
                          />
                        </div>
                      ) : null}
                    </div>
                  )}

                  {showChart[index] && (
                    <div className="machine-exercise-card__chart">
                      <div className="machine-exercise-card__chart-heading">
                        <div>
                          <span>Your progress</span>
                          <h4>Best set over time</h4>
                        </div>
                        <div className="machine-exercise-card__legend">
                          <i aria-hidden="true" />
                          Weight
                        </div>
                      </div>
                      <p className="machine-exercise-card__chart-help">
                        One point per workout shows your heaviest set. Tap a
                        point to see its weight and reps.
                      </p>
                      {chartData[index]?.length ? (
                        <MachineExerciseChart data={chartData[index]} />
                      ) : (
                        <IonText color="medium">
                          No workout data for this exercise yet.
                        </IonText>
                      )}
                    </div>
                  )}
                </IonCardContent>
              </IonCard>
            );
          })}
        </div>
      </IonContent>
    </IonPage>
  );
};

export default MachineItem;
